import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { orpc, rpcClient } from 'src/api/rpc/client';
import type {
  ListWorkflowDefinitionSummariesResponse,
  WorkflowDefinitionResponse,
  WorkflowResponse,
  UpdateWorkflowRequest,
  ListWorkflowRunsForDefinitionResponse,
  ListRerunsForRunResponse,
  WorkflowRunResponse,
  CreateWorkflowDefinitionRequest,
  UpdateWorkflowDefinitionRequest,
  ListStepTypesResponse,
  ListActionWorkflowOptionsResponse,
  StepRunResponse,
  ToolCallRunResponse,
  RetryStepResponse,
  RestartFromStepResponse,
  ResolveGuardrailReviewResponse,
  GetEntityWorkflowContextResponse,
  WorkflowTestCase,
  CheckTestCaseCompatibilityResponse,
  WorkflowGroupResponse,
  WorkflowGroupSummary,
  CreateWorkflowGroupRequest,
  UpdateWorkflowGroupRequest,
} from '@erp/rpc';
import type { SystemEntity } from '@erp/shared-types';

/**
 * List workflow definition summaries (with node count and last run)
 */
export function useListWorkflowDefinitionSummaries(params?: {
  limit?: number;
  offset?: number;
  includeDeleted?: boolean;
  search?: string;
  mine?: boolean;
  published?: boolean;
  paused?: boolean;
  scheduled?: boolean;
  event?: boolean;
  tags?: string[];
  sortBy?: 'name' | 'createdAt' | 'updatedAt';
  sortDirection?: 'asc' | 'desc';
}) {
  return useQuery<ListWorkflowDefinitionSummariesResponse>({
    queryKey: ['workflow-definitions', 'summaries', params],
    queryFn: async () => {
      return await rpcClient.workflows.listDefinitionSummaries({
        limit: params?.limit,
        offset: params?.offset,
        includeDeleted: params?.includeDeleted,
        search: params?.search,
        mine: params?.mine,
        published: params?.published,
        paused: params?.paused,
        scheduled: params?.scheduled,
        event: params?.event,
        tags: params?.tags,
        sortBy: params?.sortBy,
        sortDirection: params?.sortDirection,
      });
    },
  });
}

export function useListWorkflowGroups(params?: {
  limit?: number;
  offset?: number;
}) {
  return useQuery<{ groups: WorkflowGroupSummary[]; total: number }>({
    queryKey: ['workflow-groups', 'list', params],
    queryFn: async () => {
      return await rpcClient.workflows.listGroups({
        limit: params?.limit,
        offset: params?.offset,
      });
    },
  });
}

export function useGetWorkflowGroup(workflowGroupId?: string) {
  return useQuery<WorkflowGroupResponse>({
    queryKey: ['workflow-groups', workflowGroupId],
    queryFn: async () => {
      if (!workflowGroupId) {
        throw new Error('Workflow group ID is required');
      }
      return await rpcClient.workflows.getGroup({
        workflowGroupId,
      });
    },
    enabled: !!workflowGroupId,
  });
}

export function useListWorkflowGroupsForWorkflow(workflowId?: string) {
  return useQuery<{ groups: WorkflowGroupSummary[] }>({
    queryKey: ['workflow-groups', 'for-workflow', workflowId],
    queryFn: async () => {
      if (!workflowId) {
        throw new Error('Workflow ID is required');
      }
      return await rpcClient.workflows.listGroupsForWorkflow({
        workflowId,
      });
    },
    enabled: !!workflowId,
  });
}

/**
 * Get a workflow definition by ID (returns the specific version's graph).
 * Lineage-level fields (name, tags, isActive, orchestratorType) live on the
 * parent Workflow — fetch via `useGetWorkflow(definition.workflowId)`.
 */
export function useGetWorkflowDefinition(workflowDefinitionId?: string) {
  // Use orpc's queryOptions so the cache key matches what the route loader
  // seeds via `queryClient.ensureQueryData(orpc.workflows.getDefinition...)`.
  // Manual `['workflow-definitions', id]` keys used to double-cache — that's
  // how saved edits could display stale graphs until a page refresh.
  return useQuery({
    ...orpc.workflows.getDefinition.queryOptions({
      input: { workflowDefinitionId: workflowDefinitionId ?? '' },
    }),
    enabled: !!workflowDefinitionId,
  });
}

/**
 * Get the Workflow (lineage-level row) by its id. Carries name, description,
 * tags, isActive, orchestratorType, pinnedVersionId, etc.
 */
export function useGetWorkflow(workflowId?: string) {
  return useQuery({
    ...orpc.workflows.getWorkflow.queryOptions({
      input: { workflowId: workflowId ?? '' },
    }),
    enabled: !!workflowId,
  });
}

/**
 * Update lineage-level fields on a Workflow (name, description, tags,
 * isActive, orchestratorType, etc). Version graph edits go through
 * `useUpdateWorkflowDefinition`.
 */
export function useUpdateWorkflow() {
  const queryClient = useQueryClient();
  return useMutation<WorkflowResponse, Error, UpdateWorkflowRequest>({
    mutationFn: async (input) => {
      return await rpcClient.workflows.updateWorkflow(input);
    },
    onSuccess: (workflow) => {
      // Seed the orpc-keyed cache directly. The route loader uses
      // `orpc.workflows.getWorkflow.queryOptions(...)` which has its own
      // query key — writing to a manual `['workflow', id]` key wouldn't
      // reach the loader's cache, so the overview/editor would keep seeing
      // stale data after save until a hard refresh.
      queryClient.setQueryData(
        orpc.workflows.getWorkflow.queryOptions({
          input: { workflowId: workflow.id },
        }).queryKey,
        workflow,
      );
      queryClient.invalidateQueries({
        queryKey: ['workflow-definitions', 'summaries'],
      });
    },
  });
}

/**
 * List runs for a workflow definition
 */
export function useListWorkflowRunsForDefinition(
  workflowDefinitionId?: string,
  options?: {
    limit?: number;
    offset?: number;
    status?: WorkflowRunResponse['status'];
    triggeredByType?: 'user' | 'system';
    triggeredFrom?: string;
    triggeredTo?: string;
    search?: string;
    testCaseId?: string;
    refetchInterval?: number | false;
    enabled?: boolean;
  },
) {
  return useQuery<ListWorkflowRunsForDefinitionResponse>({
    queryKey: [
      'workflow-runs',
      workflowDefinitionId,
      options?.limit,
      options?.offset,
      options?.status,
      options?.triggeredByType,
      options?.triggeredFrom,
      options?.triggeredTo,
      options?.search,
      options?.testCaseId,
    ],
    queryFn: async () => {
      if (!workflowDefinitionId) {
        throw new Error('Workflow definition ID is required');
      }
      return await rpcClient.workflows.listRunsForDefinition({
        workflowDefinitionId,
        limit: options?.limit,
        offset: options?.offset,
        status: options?.status,
        triggeredByType: options?.triggeredByType,
        triggeredFrom: options?.triggeredFrom,
        triggeredTo: options?.triggeredTo,
        search: options?.search,
        testCaseId: options?.testCaseId,
      });
    },
    enabled: !!workflowDefinitionId && (options?.enabled ?? true),
    refetchInterval: options?.refetchInterval,
  });
}

/**
 * List rerun children for a workflow run.
 *
 * To defer the fetch, either:
 * - leave `workflowRunId` undefined, or
 * - pass `options.enabled: false`.
 */
export function useListRerunsForRun(
  workflowRunId?: string,
  options?: { enabled?: boolean },
) {
  return useQuery<ListRerunsForRunResponse>({
    queryKey: ['workflow-runs', 'reruns', workflowRunId],
    queryFn: async () => {
      if (!workflowRunId) {
        throw new Error('Workflow run ID is required');
      }
      return await rpcClient.workflows.listRerunsForRun({ workflowRunId });
    },
    enabled: !!workflowRunId && (options?.enabled ?? true),
  });
}

/**
 * Get a workflow run by ID
 */
export function useGetWorkflowRun(
  workflowRunId?: string,
  options?: { refetchInterval?: number | false },
) {
  return useQuery<WorkflowRunResponse>({
    queryKey: ['workflow-runs', 'detail', workflowRunId],
    queryFn: async () => {
      if (!workflowRunId) {
        throw new Error('Workflow run ID is required');
      }
      return await rpcClient.workflows.getRun({
        workflowRunId,
      });
    },
    enabled: !!workflowRunId,
    refetchInterval: options?.refetchInterval,
  });
}

/**
 * Create a new workflow definition
 */
export function useCreateWorkflowDefinition() {
  const queryClient = useQueryClient();

  return useMutation<
    WorkflowDefinitionResponse,
    Error,
    CreateWorkflowDefinitionRequest
  >({
    mutationFn: async (input) => {
      return await rpcClient.workflows.createDefinition(input);
    },
    onSuccess: () => {
      // Invalidate the summaries list to show the new workflow
      queryClient.invalidateQueries({
        queryKey: ['workflow-definitions', 'summaries'],
      });
      queryClient.invalidateQueries({
        queryKey: ['workflow-actions'],
      });
    },
  });
}

export function useCreateWorkflowGroup() {
  const queryClient = useQueryClient();

  return useMutation<WorkflowGroupResponse, Error, CreateWorkflowGroupRequest>({
    mutationFn: async (input) => {
      return await rpcClient.workflows.createGroup(input);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ['workflow-groups', 'list'],
      });
      queryClient.invalidateQueries({
        queryKey: ['workflow-groups', data.id],
      });
    },
  });
}

/**
 * Update a workflow definition
 */
export function useUpdateWorkflowDefinition() {
  const queryClient = useQueryClient();

  return useMutation<
    WorkflowDefinitionResponse,
    Error,
    UpdateWorkflowDefinitionRequest
  >({
    mutationFn: async (input) => {
      return await rpcClient.workflows.updateDefinition(input);
    },
    onSuccess: (data) => {
      // Seed the orpc-keyed cache — the same key the route loader uses via
      // `orpc.workflows.getDefinition.queryOptions(...)`. Manual keys like
      // `['workflow-definitions', id]` produced a second, ghost cache entry
      // that the loader never read, which is how saved graph edits kept
      // showing the pre-save state until a page refresh.
      queryClient.setQueryData(
        orpc.workflows.getDefinition.queryOptions({
          input: { workflowDefinitionId: data.id },
        }).queryKey,
        data,
      );
      queryClient.invalidateQueries({
        queryKey: ['workflow-definitions', 'summaries'],
      });
      queryClient.invalidateQueries({
        queryKey: ['workflow-actions'],
      });
    },
  });
}

export function useUpdateWorkflowGroup() {
  const queryClient = useQueryClient();

  return useMutation<WorkflowGroupResponse, Error, UpdateWorkflowGroupRequest>({
    mutationFn: async (input) => {
      return await rpcClient.workflows.updateGroup(input);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ['workflow-groups', 'list'],
      });
      queryClient.invalidateQueries({
        queryKey: ['workflow-groups', data.id],
      });
      queryClient.invalidateQueries({
        queryKey: ['workflow-groups', 'for-workflow'],
      });
    },
  });
}

/**
 * List available step types for workflow definitions
 */
export function useListStepTypes(workflowDefinitionId?: string) {
  return useQuery<ListStepTypesResponse>({
    queryKey: ['workflow-step-types', workflowDefinitionId],
    queryFn: async () => {
      return await rpcClient.workflows.listStepTypes({
        workflowDefinitionId,
      });
    },
    staleTime: 1000 * 30, // inUse status changes with step mutations, keep fresh
  });
}

export function useListActionWorkflowOptions() {
  return useQuery<ListActionWorkflowOptionsResponse>({
    queryKey: ['workflow-actions'],
    queryFn: async () => {
      return await rpcClient.workflows.listActions({});
    },
    staleTime: 1000 * 60,
  });
}

/**
 * Get step runs for a workflow run
 */
export function useGetStepRuns(
  workflowRunId?: string,
  options?: { refetchInterval?: number | false },
) {
  return useQuery<{ stepRuns: StepRunResponse[] }>({
    queryKey: ['workflow-step-runs', workflowRunId],
    queryFn: async () => {
      if (!workflowRunId) {
        throw new Error('Workflow run ID is required');
      }
      return await rpcClient.workflows.getStepRuns({
        workflowRunId,
      });
    },
    enabled: !!workflowRunId,
    refetchInterval: options?.refetchInterval,
  });
}

/**
 * Get agent tool-call StepRuns recorded against a workflow run (computer-use
 * operator iterations from remote-desktop steps). The trace nests these under
 * their `parentStepRunId` step. Separate from useGetStepRuns, which returns
 * only the workflow's own steps.
 */
export function useGetWorkflowRunToolCalls(
  workflowRunId?: string,
  options?: { refetchInterval?: number | false },
) {
  return useQuery<{ toolCalls: ToolCallRunResponse[] }>({
    queryKey: ['workflow-run-tool-calls', workflowRunId],
    queryFn: async () => {
      if (!workflowRunId) {
        throw new Error('Workflow run ID is required');
      }
      return await rpcClient.workflows.getWorkflowRunToolCalls({
        workflowRunId,
      });
    },
    enabled: !!workflowRunId,
    refetchInterval: options?.refetchInterval,
  });
}

export function useGetStepRunLogs(stepRunId?: string) {
  return useQuery({
    queryKey: ['workflow-step-run-logs', stepRunId],
    queryFn: async () => {
      if (!stepRunId) {
        throw new Error('Step run ID is required');
      }
      return await rpcClient.workflows.getStepRunLogs({ stepRunId });
    },
    enabled: !!stepRunId,
  });
}

export function useGetStepRunSandbox(
  stepRunId?: string,
  options?: { refetchInterval?: number | false },
) {
  return useQuery({
    queryKey: ['workflow-step-run-sandbox', stepRunId],
    queryFn: async () => {
      if (!stepRunId) {
        throw new Error('Step run ID is required');
      }
      return await rpcClient.workflows.getStepRunSandbox({ stepRunId });
    },
    enabled: !!stepRunId,
    refetchInterval: options?.refetchInterval,
  });
}

export function useGetStepRunSandboxLogs(
  stepRunId?: string,
  options?: { refetchInterval?: number | false },
) {
  return useQuery({
    queryKey: ['workflow-step-run-sandbox-logs', stepRunId],
    queryFn: async () => {
      if (!stepRunId) {
        throw new Error('Step run ID is required');
      }
      return await rpcClient.workflows.getStepRunSandboxLogs({ stepRunId });
    },
    enabled: !!stepRunId,
    refetchInterval: options?.refetchInterval,
  });
}

export function useGetWorkflowRunLogs(
  workflowRunId?: string,
  options?: { refetchInterval?: number | false },
) {
  return useQuery({
    queryKey: ['workflow-run-logs', workflowRunId],
    queryFn: async () => {
      if (!workflowRunId) {
        throw new Error('Workflow run ID is required');
      }
      return await rpcClient.workflows.getWorkflowRunLogs({ workflowRunId });
    },
    enabled: !!workflowRunId,
    refetchInterval: options?.refetchInterval,
  });
}

/**
 * Get child workflow runs for a parent workflow run
 */
export function useGetChildWorkflowRuns(
  parentWorkflowRunId?: string,
  options?: { refetchInterval?: number | false },
) {
  return useQuery<{ childWorkflowRuns: WorkflowRunResponse[] }>({
    queryKey: ['workflow-child-runs', parentWorkflowRunId],
    queryFn: async () => {
      if (!parentWorkflowRunId) {
        throw new Error('Parent workflow run ID is required');
      }
      return await rpcClient.workflows.getChildWorkflowRuns({
        parentWorkflowRunId,
      });
    },
    enabled: !!parentWorkflowRunId,
    refetchInterval: options?.refetchInterval,
  });
}

/**
 * Get workflow run context for an entity (used by entity detail rerun actions)
 */
export function useEntityWorkflowContext(
  entityType?: SystemEntity,
  entityId?: string,
) {
  return useQuery<GetEntityWorkflowContextResponse>({
    queryKey: ['workflow-entity-context', entityType, entityId],
    queryFn: async () => {
      if (!entityType || !entityId) {
        throw new Error('Entity type and entity ID are required');
      }

      return await rpcClient.workflows.getEntityWorkflowContext({
        entityType,
        entityId,
      });
    },
    enabled: !!entityType && !!entityId,
  });
}

/**
 * Cancel an active workflow run
 */
export function useCancelWorkflowRun() {
  const queryClient = useQueryClient();

  return useMutation<WorkflowRunResponse, Error, { workflowRunId: string }>({
    mutationFn: async ({ workflowRunId }) => {
      return await rpcClient.workflows.cancelWorkflowRun({ workflowRunId });
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ['workflow-runs', 'detail', data.id],
      });
      queryClient.invalidateQueries({ queryKey: ['workflow-runs'] });
    },
  });
}

/**
 * Retry a failed or timed_out step
 */
export function useRetryStep() {
  const queryClient = useQueryClient();

  return useMutation<
    RetryStepResponse,
    Error,
    {
      workflowRunId: string;
      stepRunId: string;
      inputData?: Record<string, unknown>;
    }
  >({
    mutationFn: async ({ workflowRunId, stepRunId, inputData }) => {
      return await rpcClient.workflows.retryStep({
        workflowRunId,
        stepRunId,
        inputData,
      });
    },
    onSuccess: (data) => {
      // Invalidate the step runs and workflow run
      queryClient.invalidateQueries({
        queryKey: ['workflow-step-runs', data.workflowRun.id],
      });
      queryClient.invalidateQueries({
        queryKey: ['workflow-runs', 'detail', data.workflowRun.id],
      });
      queryClient.invalidateQueries({
        queryKey: ['workflow-runs'],
      });
      queryClient.invalidateQueries({
        queryKey: ['workflow-entity-context'],
      });
    },
  });
}

/**
 * Resolve a guardrail review (confirm or dismiss)
 */
export function useResolveGuardrailReview() {
  const queryClient = useQueryClient();

  return useMutation<
    ResolveGuardrailReviewResponse,
    Error,
    {
      workflowRunId: string;
      stepRunId: string;
      decision: 'confirmed' | 'dismissed';
      comment?: string;
    }
  >({
    mutationFn: async ({ workflowRunId, stepRunId, decision, comment }) => {
      return await rpcClient.workflows.resolveGuardrailReview({
        workflowRunId,
        stepRunId,
        decision,
        comment,
      });
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ['workflow-step-runs', data.workflowRun.id],
      });
      queryClient.invalidateQueries({
        queryKey: ['workflow-runs', 'detail', data.workflowRun.id],
      });
      queryClient.invalidateQueries({
        queryKey: ['workflow-runs'],
      });
    },
  });
}

/**
 * Restart workflow from a specific step
 */
export function useRestartFromStep() {
  const queryClient = useQueryClient();

  return useMutation<
    RestartFromStepResponse,
    Error,
    {
      workflowRunId: string;
      workflowStepId: string;
      inputData?: Record<string, unknown>;
    }
  >({
    mutationFn: async ({ workflowRunId, workflowStepId, inputData }) => {
      return await rpcClient.workflows.restartFromStep({
        workflowRunId,
        workflowStepId,
        inputData,
      });
    },
    onSuccess: (data) => {
      // Invalidate the step runs and workflow run
      queryClient.invalidateQueries({
        queryKey: ['workflow-step-runs', data.workflowRun.id],
      });
      queryClient.invalidateQueries({
        queryKey: ['workflow-runs', 'detail', data.workflowRun.id],
      });
      queryClient.invalidateQueries({
        queryKey: ['workflow-runs'],
      });
      queryClient.invalidateQueries({
        queryKey: ['workflow-entity-context'],
      });
    },
  });
}

/**
 * Duplicate a workflow definition
 */
export function useDuplicateWorkflowDefinition() {
  const queryClient = useQueryClient();

  return useMutation<
    WorkflowDefinitionResponse,
    Error,
    { workflowDefinitionId: string; name: string }
  >({
    mutationFn: async (input) => {
      return await rpcClient.workflows.duplicateDefinition(input);
    },
    onSuccess: () => {
      // Invalidate the summaries list to show the new workflow
      queryClient.invalidateQueries({
        queryKey: ['workflow-definitions', 'summaries'],
      });
    },
  });
}

/**
 * Test a single workflow step in isolation (admin only)
 */
export function useTestStep() {
  return useMutation<
    {
      success: boolean;
      output?: Record<string, unknown>;
      emittedEvents?: Array<{ eventType: string; data: unknown }>;
      error?: string;
      durationMs?: number;
    },
    Error,
    {
      stepType: string;
      input: Record<string, unknown>;
      config: Record<string, unknown>;
      timeoutMs?: number;
    }
  >({
    mutationFn: async (params) => {
      return await rpcClient.workflows.testStep(params);
    },
  });
}

/**
 * Trigger a scheduled workflow to run immediately.
 * The mutation fires the trigger RPC; polling for completion is handled
 * separately in onSuccess so the RPC call stays clean.
 */
/**
 * Delete a workflow definition (soft delete)
 */
export function useDeleteWorkflowDefinition() {
  const queryClient = useQueryClient();

  return useMutation<
    { success: true },
    Error,
    { workflowDefinitionId: string }
  >({
    mutationFn: async (input) => {
      return await rpcClient.workflows.deleteDefinition(input);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['workflow-definitions', 'summaries'],
      });
      queryClient.invalidateQueries({
        queryKey: ['workflow-definitions'],
      });
    },
  });
}

/**
 * Delete a whole workflow (lineage) — cascades to every version, run, and
 * step under it. Used by the workflows-list row action, which operates on
 * workflow ids (not definition ids).
 */
export function useDeleteWorkflow() {
  const queryClient = useQueryClient();

  return useMutation<{ success: true }, Error, { workflowId: string }>({
    mutationFn: async (input) => {
      return await rpcClient.workflows.deleteWorkflow(input);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['workflow-definitions', 'summaries'],
      });
      queryClient.invalidateQueries({
        queryKey: ['workflow-definitions'],
      });
      queryClient.invalidateQueries({ queryKey: ['workflow-groups'] });
    },
  });
}

export function useTriggerWorkflow() {
  const queryClient = useQueryClient();

  return useMutation<
    WorkflowRunResponse,
    Error,
    {
      workflowDefinitionId: string;
      initialInputs?: Record<string, unknown>;
      rerunOfWorkflowRunId?: string;
    }
  >({
    mutationFn: (input) => rpcClient.workflows.trigger(input),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['workflow-runs', variables.workflowDefinitionId],
      });
      queryClient.invalidateQueries({
        queryKey: ['workflow-definitions', 'summaries'],
      });
    },
  });
}

export function useRerunFromRun(workflowDefinitionId?: string) {
  const queryClient = useQueryClient();

  return useMutation<WorkflowRunResponse, Error, { workflowRunId: string }>({
    mutationFn: (input) => rpcClient.workflows.rerunFromRun(input),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['workflow-runs', 'detail', variables.workflowRunId],
      });
      queryClient.invalidateQueries({
        queryKey: ['workflow-runs', 'reruns', variables.workflowRunId],
      });
      if (workflowDefinitionId) {
        queryClient.invalidateQueries({
          queryKey: ['workflow-runs', workflowDefinitionId],
        });
      }
      queryClient.invalidateQueries({
        queryKey: ['workflow-definitions', 'summaries'],
      });
    },
  });
}

export function useTestDraftWithRun(workflowDefinitionId?: string) {
  const queryClient = useQueryClient();

  return useMutation<
    WorkflowRunResponse,
    Error,
    { draftDefinitionId: string; sourceRunId: string }
  >({
    mutationFn: (input) => rpcClient.workflows.testDraftWithRun(input),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['workflow-runs', variables.draftDefinitionId],
      });
      if (workflowDefinitionId) {
        queryClient.invalidateQueries({
          queryKey: ['workflow-runs', workflowDefinitionId],
        });
      }
      queryClient.invalidateQueries({
        queryKey: ['workflow-definitions', 'summaries'],
      });
    },
  });
}

export function useTriggerScheduleNow() {
  const queryClient = useQueryClient();

  return useMutation<
    { success: boolean; message: string; workflowRunId?: string },
    Error,
    { workflowDefinitionId: string }
  >({
    mutationFn: (input) => rpcClient.workflows.triggerScheduleNow(input),
    onSuccess: (data, variables) => {
      // Invalidate the workflow runs list to show the new run
      queryClient.invalidateQueries({
        queryKey: ['workflow-runs', variables.workflowDefinitionId],
      });
      // Also invalidate summaries to update the last run time
      queryClient.invalidateQueries({
        queryKey: ['workflow-definitions', 'summaries'],
      });
    },
  });
}

export function useTriggerObserveInitialIngest() {
  const queryClient = useQueryClient();

  return useMutation<
    { success: boolean; message: string; workflowRunIds: string[] },
    Error,
    { workflowDefinitionId: string }
  >({
    mutationFn: (input) =>
      rpcClient.workflows.triggerObserveInitialIngest(input),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['workflow-runs', variables.workflowDefinitionId],
      });
      queryClient.invalidateQueries({
        queryKey: ['workflow-definitions', 'summaries'],
      });
    },
  });
}

// ── Temporal Status Sync ────────────────────────────────────────────

/**
 * Sync active workflow run statuses with Temporal.
 * Called when viewing runs for a Temporal-orchestrated workflow.
 */
export function useSyncTemporalRunStatuses() {
  const queryClient = useQueryClient();

  return useMutation<
    {
      statuses: Record<
        string,
        { status: WorkflowRunResponse['status']; stopReason?: string }
      >;
    },
    Error,
    { workflowRunIds: string[] }
  >({
    mutationFn: (input) => rpcClient.workflows.syncTemporalRunStatuses(input),
    onSuccess: (data) => {
      if (Object.keys(data.statuses).length > 0) {
        // Invalidate runs list to pick up updated statuses
        queryClient.invalidateQueries({
          queryKey: ['workflow-runs'],
        });
      }
    },
  });
}

// ── Orchestrator Health ─────────────────────────────────────────────

export function useOrchestratorHealth() {
  return useQuery<{ rabbitmq: boolean; temporal: boolean }>({
    queryKey: ['orchestrator-health'],
    queryFn: () => rpcClient.workflows.orchestratorHealth({}),
    staleTime: 30_000, // Cache for 30s
    refetchInterval: 60_000, // Refresh every 60s
  });
}

// ── Test Case Hooks ─────────────────────────────────────────────────

export function useListTestCases(workflowDefinitionId?: string) {
  return useQuery<{ testCases: WorkflowTestCase[] }>({
    queryKey: ['workflow-test-cases', workflowDefinitionId],
    queryFn: async () => {
      if (!workflowDefinitionId) throw new Error('Missing definition ID');
      return rpcClient.workflows.listTestCases({ workflowDefinitionId });
    },
    enabled: !!workflowDefinitionId,
  });
}

export function useCreateTestCase() {
  const queryClient = useQueryClient();

  return useMutation<
    WorkflowTestCase,
    Error,
    {
      workflowDefinitionId: string;
      name: string;
      description?: string;
      eventData: Record<string, unknown>;
    }
  >({
    mutationFn: (input) => rpcClient.workflows.createTestCase(input),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['workflow-test-cases', variables.workflowDefinitionId],
      });
    },
  });
}

export function useUpdateTestCase(workflowDefinitionId?: string) {
  const queryClient = useQueryClient();

  return useMutation<
    WorkflowTestCase,
    Error,
    {
      testCaseId: string;
      name?: string;
      description?: string | null;
      eventData?: Record<string, unknown>;
    }
  >({
    mutationFn: (input) => rpcClient.workflows.updateTestCase(input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['workflow-test-cases', workflowDefinitionId],
      });
    },
  });
}

export function useDeleteTestCase(workflowDefinitionId?: string) {
  const queryClient = useQueryClient();

  return useMutation<{ success: true }, Error, { testCaseId: string }>({
    mutationFn: (input) => rpcClient.workflows.deleteTestCase(input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['workflow-test-cases', workflowDefinitionId],
      });
    },
  });
}

export function useRunTestCase(workflowDefinitionId?: string) {
  const queryClient = useQueryClient();

  return useMutation<WorkflowRunResponse, Error, { testCaseId: string }>({
    mutationFn: (input) => rpcClient.workflows.runTestCase(input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['workflow-test-cases', workflowDefinitionId],
      });
      queryClient.invalidateQueries({
        queryKey: ['workflow-runs', workflowDefinitionId],
      });
    },
  });
}

export function useCheckTestCaseCompatibility() {
  return useMutation<
    CheckTestCaseCompatibilityResponse,
    Error,
    {
      workflowDefinitionId: string;
      steps: Array<Record<string, unknown>>;
    }
  >({
    mutationFn: (input) =>
      rpcClient.workflows.checkTestCaseCompatibility(
        input as Parameters<
          typeof rpcClient.workflows.checkTestCaseCompatibility
        >[0],
      ),
  });
}
